import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { Boxes, Download, FileText, List, Plus, Trash2 } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  FilterSelect,
  Modal,
  Page,
  PageHeader,
  SelectField,
  TD,
  TH,
  THead,
  RowActions,
  TR,
  Table,
  TableSkeleton,
  TextInput,
  Toolbar,
  useToast,
} from "@/ui";
import type { ProductDto } from "@gatiq/shared";
import { api, ApiClientError, freshSession } from "@/lib/api";
import { useCan } from "@/auth/access";
import { useTenant } from "@/tenant/TenantProvider";
import { useOrgRoles } from "@/auth/useOrgRoles";

/**
 * Print runs of reward codes — the supply side of the whole scheme.
 *
 * Every number on this screen is money. A batch of 10,000 codes at 40 QCoin is a
 * 4,00,000 QCoin promise the moment it is issued, and most of that promise is
 * sitting on shop shelves where the org cannot see it. So the list leads with
 * what is still owed rather than with how many codes were made, and the two
 * irreversible actions — issuing and voiding — both state the amount in QCoin
 * before they will proceed.
 */
interface Batch {
  id: string;
  label: string;
  productId: string | null;
  productName: string | null;
  productSku: string | null;
  count: number;
  pointsPerCode: number;
  status: "active" | "void";
  /** Printed while dispatch was on (D-20). */
  tracked?: boolean;
  cartonSize?: number | null;
  used: number;
  unscanned: number;
  outstanding: number;
  createdAt: string;
}

interface SheetInfo {
  label: string;
  totalCodes: number;
  files: number;
  perPage: Record<string, number>;
  perPageWithCaption: Record<string, number>;
  sizes: { id: string; mm: number; label: string }[];
  defaultSize: string;
  captionMax: number;
  customMmMin: number;
  customMmMax: number;
}

const EMPTY_FORM = { productId: "", label: "", count: "", pointsPerCode: "", cartonSize: "" };

/** Stickers per A4 sheet — comes from the server so it is always in sync. */
const DEFAULT_PER_PAGE = 60;

/**
 * How much paper this is, which is what a printer asks first.
 *
 * The promo line costs a row of text under every sticker, so a non-empty
 * caption must be priced with the caption grid — otherwise the screen promises
 * fewer sheets than the file actually delivers.
 */
function sheetPages(
  info: SheetInfo,
  size: string,
  caption: string,
  customPerPage: number | null = null,
): string {
  const perPage = customPerPage ??
    (caption.trim()
      ? (info.perPageWithCaption ?? info.perPage)[size]
      : info.perPage[size]) ?? DEFAULT_PER_PAGE;
  const pages = Math.ceil(info.totalCodes / perPage);
  return `${pages.toLocaleString("en-IN")} A4 ${pages === 1 ? "sheet" : "sheets"}`;
}

/** The server names the file; the browser only honours it on a navigation. */
function filenameFrom(res: Response): string | null {
  const match = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "");
  return match?.[1] ?? null;
}

/** Indian digit grouping — 4,00,000 QCoin rather than 400,000 QCoin. */
function qcoin(n: number): string {
  return `${n.toLocaleString("en-IN")} QCoin`;
}

export function RewardBatchesPage() {
  const toast = useToast();
  // D-20: batches printed while dispatch is on can be packed in cartons.
  const dispatchOn = Boolean(useOrgRoles()?.features.dispatch);
  const can = useCan();
  const canIssue = can("rewards", "create");
  const canWithdraw = can("rewards", "delete");
  const { payload } = useTenant();
  const navigate = useNavigate();
  const { orgSlug } = useParams();
  const [batches, setBatches] = useState<Batch[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [productFilter, setProductFilter] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [voidTarget, setVoidTarget] = useState<Batch | null>(null);
  const [voiding, setVoiding] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<Batch | null>(null);
  const [archiving, setArchiving] = useState(false);

  const [sheets, setSheets] = useState<{ batch: Batch; info: SheetInfo } | null>(null);
  const [busyFile, setBusyFile] = useState<string | null>(null);
  const [size, setSize] = useState("ylarge");
  const [useCustom, setUseCustom] = useState(false);
  /** Free-text sticker width in mm, shown when no preset is selected. */
  const [sizeText, setSizeText] = useState("");
  /** Stickers per A4 sheet for the custom size, fetched from the server. */
  const [customPerPage, setCustomPerPage] = useState<number | null>(null);
  /** Optional promo line printed under the value on every sticker. */
  const [caption, setCaption] = useState("");

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await api.get<{ items: Batch[] }>("/org/rewards/batches");
      setBatches(res.items);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load batches.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
    api
      .get<{ items: ProductDto[] }>("/org/products")
      .then((res) => setProducts(res.items))
      .catch(() => setProducts([])); // No products just means typing the name by hand.
  }, [load]);

  const activeProducts = products.filter((p) => p.status === "active");

  function pickProduct(productId: string): void {
    const p = products.find((x) => x.id === productId);
    setForm((f) => ({
      ...f,
      productId,
      label: p ? p.name : f.label,
      pointsPerCode: p ? String(p.pointsPerCode) : f.pointsPerCode,
    }));
  }

  const count = Number(form.count);
  const value = Number(form.pointsPerCode);
  const liability = Number.isFinite(count) && Number.isFinite(value) ? count * value : 0;

  async function issue(e: FormEvent): Promise<void> {
    e.preventDefault();
    setFormError(null);
    // The server's limits, said in words, before anything is sent.
    const problem = !form.label.trim() && !form.productId
      ? "Choose a product or name the batch."
      : !(Number.isInteger(count) && count >= 1 && count <= 20_000)
        ? "Enter between 1 and 20,000 codes."
        : !(Number.isInteger(value) && value >= 1 && value <= 100_000)
          ? "Enter a value between 1 and 100,000 points."
          : form.label.trim().length > 80
            ? "Keep the name under 80 characters."
            : null;
    if (problem) {
      setFormError(problem);
      return;
    }
    setSaving(true);
    try {
      await api.post("/org/rewards/batches", {
        ...(form.productId ? { productId: form.productId } : {}),
        label: form.label.trim(),
        count,
        pointsPerCode: value,
        ...(dispatchOn && Number(form.cartonSize) > 0 ? { cartonSize: Number(form.cartonSize) } : {}),
      });
      toast.success(`${count.toLocaleString("en-IN")} codes created.`);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      setFormError(
        err instanceof ApiClientError ? err.message : "Could not create the batch.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function voidBatch(): Promise<void> {
    if (!voidTarget) return;
    setVoiding(true);
    try {
      const res = await api.del<{ reversed: number }>(`/org/rewards/batches/${voidTarget.id}`);
      toast.success(
        res.reversed > 0
          ? `Batch withdrawn. ${res.reversed.toLocaleString("en-IN")} scans reversed.`
          : "Batch withdrawn.",
      );
      setVoidTarget(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not withdraw the batch.");
    } finally {
      setVoiding(false);
    }
  }

  async function archiveBatch(): Promise<void> {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await api.del(`/org/rewards/batches/${archiveTarget.id}/archive`);
      toast.success("Withdrawn batch removed from the working list. Its audit and code history are retained.");
      setArchiveTarget(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not delete the batch.");
    } finally {
      setArchiving(false);
    }
  }

  async function openSheets(batch: Batch): Promise<void> {
    try {
      const info = await api.get<SheetInfo>(`/org/rewards/batches/${batch.id}/labels`);
      setSize(info.defaultSize);
      setSizeText("");
      setUseCustom(false);
      setCaption("");
      setSheets({ batch, info });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not prepare the sheets.");
    }
  }

  /*
   * Fetched rather than navigated to, so the wait is visible.
   *
   * Sheets are built on demand — a thousand codes is a few seconds of drawing —
   * and `window.location.href` spends that time showing nothing at all, which
   * reads as a dead button and invites a second click that doubles the work.
   * Fetching lets the button say it is working, and turns a failure into a
   * message instead of a page of raw JSON where a PDF was expected.
   */
  async function download(path: string, key: string): Promise<void> {
    setBusyFile(key);
    try {
      await freshSession();
      const res = await fetch(`/api${path}`, { credentials: "include" });
      if (!res.ok) throw new Error(String(res.status));

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFrom(res) ?? "labels.pdf";
      a.click();
      // Revoked on the next tick: too early and the download never starts.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      toast.error("Could not build that file. Please try again.");
    } finally {
      setBusyFile(null);
    }
  }

  const totalOutstanding = (batches ?? [])
    .filter((b) => b.status === "active")
    .reduce((sum, b) => sum + b.outstanding, 0);

  const visibleBatches = (batches ?? []).filter(
    (b) => !productFilter || (productFilter === "none" ? !b.productId : b.productId === productFilter),
  );

  const captionTrimmed = caption.trim();
  const captionLimit = sheets?.info.captionMax ?? 90;
  /** mm if the user typed a custom width, else the chosen preset's width. */
  const customMm = (() => {
    const n = Number(sizeText);
    if (!Number.isFinite(n) || n <= 0) return null;
    const min = sheets?.info.customMmMin ?? 15;
    const max = sheets?.info.customMmMax ?? 80;
    return Math.min(max, Math.max(min, n));
  })();
  const labelsPath = (n: number) =>
    `/org/rewards/batches/${sheets!.batch.id}/labels.pdf?file=${n}&size=${size}` +
    (customMm ? `&mm=${customMm}` : "") +
    (captionTrimmed ? `&caption=${encodeURIComponent(captionTrimmed.slice(0, captionLimit))}` : "");

  // How many sheets the custom size will use — fetched so the screen never
  // quotes a count the file cannot deliver.
  useEffect(() => {
    if (!sheets || !customMm) {
      setCustomPerPage(null);
      return;
    }
    let live = true;
    setCustomPerPage(null);
    void api
      .get<{ mm: number; perPage: number }>(
        `/org/rewards/batches/${sheets.batch.id}/labels/perpage?mm=${customMm}${captionTrimmed ? "&caption=1" : ""}`,
      )
      .then((res) => {
        if (live) setCustomPerPage(res.perPage);
      })
      .catch(() => {
        if (live) setCustomPerPage(null);
      });
    return () => {
      live = false;
    };
  }, [sheets, customMm, captionTrimmed]);

  return (
    <Page bleed>
      <PageHeader
        description={
          batches && batches.length > 0
            ? `${qcoin(totalOutstanding)} still unclaimed across active batches`
            : undefined
        }
        title="Reward codes"
      />

      <Card fill>
        {canIssue || (products.length > 0 && batches && batches.length > 0) ? (
          <Toolbar
            trailing={
              canIssue ? (
                <Button icon={<Plus aria-hidden="true" className="size-4" />} onClick={() => setCreateOpen(true)}>
                  Issue new batch
                </Button>
              ) : null
            }
          >
            {products.length > 0 && batches && batches.length > 0 ? (
              <FilterSelect
                allLabel="All products"
                label="Filter by product"
                onChange={setProductFilter}
                options={[
                  ...products.map((p) => ({ value: p.id, label: `${p.name} (${p.sku})` })),
                  { value: "none", label: "No product" },
                ]}
                value={productFilter}
              />
            ) : null}
          </Toolbar>
        ) : null}
        {loading && !batches ? (
          <TableSkeleton cols={8} />
        ) : batches && batches.length > 0 ? (
          <Table>
            <THead>
              <TR>
                <TH>Batch</TH>
                <TH>Value each</TH>
                <TH align="right">Codes</TH>
                <TH align="right">Scanned</TH>
                <TH align="right">Unscanned</TH>
                <TH align="right">Outstanding</TH>
                <TH>Status</TH>
                <TH align="right" width="4.5rem">
                  <span className="sr-only">Actions</span>
                </TH>
              </TR>
            </THead>
            <tbody>
              {visibleBatches.map((b) => (
                <TR key={b.id} onClick={() => void navigate(`/${orgSlug}/admin/rewards/${b.id}`)}>
                  <TD>
                    <span className="font-medium text-ink">{b.label}</span>
                    {b.productName ? (
                      <span className="block text-xs text-ink">
                        {b.productName} <span className="font-mono text-muted">{b.productSku}</span>
                      </span>
                    ) : null}
                    <span className="block text-xs text-muted">
                      {new Date(b.createdAt).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </TD>
                  <TD>{qcoin(b.pointsPerCode)}</TD>
                  <TD align="right">{b.count.toLocaleString("en-IN")}</TD>
                  <TD align="right">{b.used.toLocaleString("en-IN")}</TD>
                  <TD align="right">{b.unscanned.toLocaleString("en-IN")}</TD>
                  <TD align="right">
                    <span className={b.outstanding > 0 ? "font-medium text-ink" : "text-muted"}>
                      {qcoin(b.outstanding)}
                    </span>
                  </TD>
                  <TD>
                    <Badge tone={b.status === "active" ? "success" : "neutral"}>
                      {b.status === "active" ? "Active" : "Withdrawn"}
                    </Badge>
                  </TD>
                  <TD align="right">
                    <RowActions
                      busy={busyFile === `csv-${b.id}` || busyFile === `cartons-${b.id}`}
                      items={[
                        { label: "View codes", icon: List, onSelect: () => void navigate(`/${orgSlug}/admin/rewards/${b.id}`) },
                        { label: "Print QR sheets", icon: FileText, disabled: b.status === "void", onSelect: () => void openSheets(b) },
                        {
                          label: "Print carton labels",
                          icon: Boxes,
                          hidden: !b.cartonSize || b.status === "void",
                          onSelect: () => void download(`/org/rewards/batches/${b.id}/cartons.pdf`, `cartons-${b.id}`),
                        },
                        {
                          label: "Download CSV",
                          icon: Download,
                          onSelect: () => void download(`/org/rewards/batches/${b.id}/codes.csv`, `csv-${b.id}`),
                        },
                        { label: "Withdraw batch", icon: Trash2, danger: true, hidden: b.status === "void" || !canWithdraw, onSelect: () => setVoidTarget(b) },
                        { label: "Delete withdrawn batch", icon: Trash2, danger: true, hidden: b.status !== "void" || !canWithdraw, onSelect: () => setArchiveTarget(b) },
                      ]}
                      label={b.label}
                    />
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState
            action={canIssue ? <Button onClick={() => setCreateOpen(true)}>Issue the first batch</Button> : undefined}
            message="A batch creates the codes that get printed on your products. Karigars scan them for points."
            title="No reward codes yet"
          />
        )}
      </Card>

      {/* ── issue ───────────────────────────────────────────────── */}
      <Modal onClose={() => setCreateOpen(false)} open={createOpen} title="Issue a new batch">
        <form className="flex flex-col gap-4" noValidate onSubmit={(e) => void issue(e)}>
          {formError ? <Alert kind="danger">{formError}</Alert> : null}

          {activeProducts.length > 0 ? (
            <SelectField
              label="Product"
              onChange={(e) => pickProduct(e.target.value)}
              options={activeProducts.map((p) => ({ value: p.id, label: `${p.name} (${p.sku})` }))}
              placeholder="No product"
              value={form.productId}
            />
          ) : null}
          <TextInput
            autoFocus={activeProducts.length === 0}
            label="Name it"
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Sept print run"
            required
            value={form.label}
          />
          <TextInput
            inputMode="numeric"
            label="How many codes"
            onChange={(e) => setForm({ ...form, count: e.target.value.replace(/\D/g, "") })}
            placeholder="10000"
            required
            value={form.count}
          />
          <TextInput
            hint={`1 point = 1 QCoin in ${payload.branding.appName}`}
            inputMode="numeric"
            label="Value of each code (points)"
            onChange={(e) =>
              setForm({ ...form, pointsPerCode: e.target.value.replace(/\D/g, "") })
            }
            placeholder="40"
            required
            value={form.pointsPerCode}
          />
          {dispatchOn ? (
            <TextInput
              hint="Leave empty to print without cartons"
              inputMode="numeric"
              label="Units per carton"
              onChange={(e) => setForm({ ...form, cartonSize: e.target.value.replace(/\D/g, "") })}
              placeholder="25"
              value={form.cartonSize}
            />
          ) : null}

          {/* The number nobody should discover after clicking. */}
          {liability > 0 ? (
            <Alert kind="warning" title={`This promises up to ${qcoin(liability)}`}>
              Once printed, every one of these codes can be scanned for {qcoin(value)}.
            </Alert>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button onClick={() => setCreateOpen(false)} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              disabled={!form.label.trim() || !(count > 0) || !(value > 0)}
              loading={saving}
              type="submit"
            >
              Issue {count > 0 ? count.toLocaleString("en-IN") : ""} codes
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── printable sheets ────────────────────────────────────── */}
      <Modal
        onClose={() => setSheets(null)}
        open={sheets !== null}
        title="Printable QR sheets"
      >
        {sheets ? (
          <div className="flex flex-col gap-4">
            {sheets.info.files > 1 ? (
              <Alert kind="info" title={`Split into ${sheets.info.files} files`}>
                {sheets.info.totalCodes.toLocaleString("en-IN")} codes is more than one file can
                hold comfortably. Each file below is a separate run of sheets.
              </Alert>
            ) : null}

            {/* Chosen before downloading, because the size is baked into the
                artwork — a printer cannot resize a QR without re-rendering it. */}
            <div>
              <p className="mb-2 text-sm font-medium text-ink">Sticker size</p>
              <div className="flex flex-wrap gap-2">
                {sheets.info.sizes.map((s) => (
                  <Button
                    key={s.id}
                    onClick={() => {
                      setSize(s.id);
                      setUseCustom(false);
                      setCustomPerPage(null);
                    }}
                    size="sm"
                    variant={size === s.id && !useCustom ? "primary" : "secondary"}
                  >
                    {s.label}
                  </Button>
                ))}
                <Button
                  onClick={() => {
                    setUseCustom(true);
                    setCustomPerPage(null);
                  }}
                  size="sm"
                  variant={useCustom ? "primary" : "secondary"}
                >
                  Custom (mm)
                </Button>
              </div>
              {useCustom ? (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    className="h-10 w-28 rounded-md border border-rule bg-raised px-3 text-sm text-ink"
                    inputMode="decimal"
                    onChange={(e) => {
                      setSizeText(e.target.value);
                      setCustomPerPage(null);
                    }}
                    placeholder="25"
                    type="number"
                    value={sizeText}
                  />
                  <span className="text-sm text-muted">mm</span>
                  <span className="ml-2 text-xs text-muted">
                    {sheets.info.customMmMin}-{sheets.info.customMmMax}mm
                  </span>
                </div>
              ) : null}
            </div>

            {/* Optional promo line, printed under the value on every sticker.
                Left blank and the sheet is exactly as it used to be. */}
            <div>
              <p className="mb-2 text-sm font-medium text-ink">
                Text under the QR code
                <span className="ml-1.5 font-normal text-muted">optional</span>
              </p>
              <input
                className="h-10 w-full rounded-md border border-rule bg-raised px-3 text-sm text-ink placeholder:text-muted/60"
                maxLength={captionLimit}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="e.g. Scan to redeem • Free delivery above 999"
                type="text"
                value={caption}
              />
              <p className="mt-1.5 flex flex-wrap items-baseline gap-x-1.5 text-xs text-muted">
                <span>Printed under the value on every sticker of this run.</span>
                <span className="tabular-nums">
                  {captionTrimmed.length}/{captionLimit}
                </span>
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {Array.from({ length: sheets.info.files }, (_, i) => i + 1).map((n) => (
                <Button
                  icon={<Download aria-hidden="true" className="size-4" />}
                  key={n}
                  loading={busyFile === `pdf-${n}`}
                  onClick={() => void download(labelsPath(n), `pdf-${n}`)}
                  variant="secondary"
                >
                  {sheets.info.files > 1 ? `File ${n} of ${sheets.info.files}` : "Download PDF"}
                </Button>
              ))}
            </div>

            <p className="text-xs text-muted">{sheetPages(sheets.info, size, caption, customPerPage)} to print.</p>
          </div>
        ) : null}
      </Modal>

      {/* ── withdraw ────────────────────────────────────────────── */}
      <ConfirmDialog
        confirmLabel={voidTarget && voidTarget.used > 0 ? "Withdraw and reverse scans" : "Withdraw batch"}
        loading={voiding}
        message={
          voidTarget
            ? voidTarget.used > 0
              ? <div className="space-y-3">
                  <p className="font-semibold text-danger">Warning: {voidTarget.used.toLocaleString("en-IN")} QR {voidTarget.used === 1 ? "code has" : "codes have"} already been scanned.</p>
                  <p>Withdrawing this batch reverses the QCoin awarded for those scans. The affected karigars will lose that QCoin, and the reversals will appear in their history.</p>
                  <p>The remaining {(voidTarget.count - voidTarget.used).toLocaleString("en-IN")} unscanned codes will stop working immediately.</p>
                </div>
              : `All ${voidTarget.count.toLocaleString("en-IN")} codes stop working immediately. None have been scanned, so no karigar loses points.`
            : ""
        }
        onClose={() => setVoidTarget(null)}
        onConfirm={() => void voidBatch()}
        open={voidTarget !== null}
        title={voidTarget ? voidTarget.used > 0 ? `Withdraw scanned batch "${voidTarget.label}"?` : `Withdraw "${voidTarget.label}"?` : ""}
      />

      <ConfirmDialog
        confirmLabel="Delete from list"
        loading={archiving}
        message={archiveTarget
          ? `Hide “${archiveTarget.label}” from the reward batch list? Its scanned-code, reversal, and audit history will be preserved.`
          : ""}
        onClose={() => setArchiveTarget(null)}
        onConfirm={() => void archiveBatch()}
        open={archiveTarget !== null}
        title="Delete withdrawn batch?"
      />
    </Page>
  );
}
